import i18next from 'i18next';
import CreatableSelect from 'react-select/creatable';
import ReactQuill from 'react-quill';
import MultiFileUploader from '../../../MultiFileUploader/MultiFileUploader';
import SpinnerButton from '../../../SpinnerButton/SpinnerButton';
import * as Yup from 'yup';
import ValidationError from '../../../ValidationError/ValidationError';
import { Markets, ReactSelectOption } from '../../../../types';
import { useTranslation } from 'react-i18next';
import { Alert, Col, Row, Form, Button } from 'react-bootstrap';
import { OPPORTUNITY_SUPPORTED_FILE_TYPES } from '../../../../utils/constants';
import { emailIsValid, randomString, removeDuplicates, strToFloat } from '../../../../utils';
import { MultiValue, SingleValue, ActionMeta } from 'react-select';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OpportunityStageEnum, RoleEnum } from '../../../../api/models/Enums';
import { useApiClient } from '../../../../hooks/useApiClient';
import { Formik } from 'formik';
import { useErrorToast } from "../../../../hooks/useErrorToast";
import { toast } from 'react-toastify';
import { Storage } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import { isMarket } from 'src/utils/markets';
import { getUploadSendDocumentsEmailTemplate } from '../utils';
import { UploadFile } from '../types';
import { useAuth } from 'src/hooks/useAuth';

export const UploadSendDocumentsPanel = ({ funnelData, sendEmailData, setSendEmailData, uploadFiles, setUploadFiles }) => {
  const { t } = useTranslation();
  const { showError } = useErrorToast();
  const { userRole } = useAuth();
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate();
  const { apiClient, handleApiResponse } = useApiClient();

  const updateFunnelData = useCallback((values: any) => {
    setSendEmailData((prev) => ({
      ...prev,
      ...values,
    }));
  }, [setSendEmailData])

  const uploadFilesToBucket = async (files) => {
    let filesToUploadList: UploadFile[] = []

    const uploadContent = {
      'uploadFiles': { data: files }
    }

    for (const type in uploadContent) {
      const fileArray: { data: File[], type: 'sendFiles' | 'uploadFiles' } = uploadContent[type]

      if (fileArray.data.length) {
        let uploadOps: any[] = []

        fileArray.data.forEach(file => {
          const ext = file.name.split('.').pop()
          const key = `opportunity_uploads/${randomString(10)}.${ext}`
          filesToUploadList.push({ type, file, filename: file.name, key })
        })

        uploadOps = filesToUploadList.map(obj => {
          return Storage.put(obj.key, obj.file, {
            contentType: obj.file.type,
            bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_SALESFORCE_IMPORT,
          });
        })

        try {
          await Promise.all(uploadOps)
        } catch (e) {
          console.error(e);
          toast.error(i18next.t('error_loading_file'))
          throw e;
        }
      }
    }

    return filesToUploadList.map(el => ({ ...el, key: `public/${el.key}` }))
  }

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        enabled: Yup.boolean(),
        to: Yup.object().when('enabled', {
          is: true,
          then: (schema) =>
            schema
              .shape({
                value: Yup.string().required(t('required_field')),
                label: Yup.string().required(t('required_field')),
              })
              .required(t('required_field'))
              .test('valid-email', t('invalid_email'), (value) => {
                return value ? emailIsValid(value.value) : false;
              }),
          otherwise: (schema) => schema.nullable(),
        }),

        cc: Yup.array().of(
          Yup.object().shape({
            value: Yup.string().test('valid-email', t('invalid_email'), (value) =>
              value ? emailIsValid(value) : true
            ),
          })
        ),
        message: Yup.string().when('enabled', {
          is: true,
          then: (schema) => schema.required(t('required_field')),
          otherwise: (schema) => schema.notRequired(),
        }),
        files: Yup.array().min(1, t('upload_file_required')).required(t('required_field')),
      }),
    [t]
  );

  const initialValues = useRef({
    enabled: sendEmailData?.enabled || false,
    to: funnelData?.account?.Email__c
      ? { value: funnelData.account.Email__c, label: funnelData.account.Email__c }
      : (sendEmailData?.to || null),
    cc: sendEmailData?.cc || [],
    message: sendEmailData?.message || getUploadSendDocumentsEmailTemplate(),
    files: sendEmailData?.files || [],
  })

  useEffect(() => {
    if (sendEmailData.to) return;

    if (initialValues.current.to) {
      setSendEmailData((prev) => ({ ...prev, to: initialValues.current.to }))
    }
  }, [])

  return (
    <div>
      {!funnelData.account ? (
        <Alert>{t('account_selection_required_to_proceed')}</Alert>
      ) : (
        <Formik
          initialValues={initialValues.current}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            updateFunnelData(values)
          }}
          enableReinitialize={true}
        >
          {({ errors, values, setFieldValue, touched, handleSubmit }) => {
            // updateFunnelData(values)
            return (
              <Form onSubmit={handleSubmit}>
                {userRole === RoleEnum.SALES_DEALER ? null
                  : (<Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        {values.enabled ? i18next.t('send_email') : i18next.t('no_send_email')}
                      </Form.Label>
                      <Form.Check
                        name="enabled"
                        id="sendEmail"
                        type="switch"
                        onChange={(e) => {
                          setSendEmailData(prev => ({ ...prev, enabled: e.target.checked }))
                          setFieldValue('enabled', e.target.checked);
                        }}
                        checked={values.enabled}
                        className="form-check-switch"
                      />
                    </Form.Group>
                  </Col>)}

                {values.enabled && (
                  <div className="mt-4">
                    <div className="my-4">
                      <hr />
                    </div>
                    <Row>
                      {/* TO recipient */}
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{i18next.t('to')}</Form.Label>
                          <CreatableSelect
                            isClearable
                            components={{ DropdownIndicator: null }}
                            placeholder="A"
                            options={values.to ? [typeof values.to === 'string' ? { value: values.to, label: values.to } : values.to] : []}
                            noOptionsMessage={() => i18next.t('add_customer_email')}
                            value={typeof values.to === 'string' ? { value: values.to, label: values.to } : values.to}
                            formatCreateLabel={(i) => (emailIsValid(i) ? `Invia a ${i}` : `${i}`)}
                            onChange={(newValue: SingleValue<ReactSelectOption>, actionMeta: ActionMeta<ReactSelectOption>) => {
                              const opt = typeof newValue === 'string' ? { value: newValue, label: newValue } : newValue;
                              if (!opt || emailIsValid(opt.value)) {
                                setFieldValue('to', opt)
                                setSendEmailData(prev => ({ ...prev, to: opt }))
                              }
                            }}
                          />
                          <ValidationError {...{ errors, touched, name: 'to' }} />
                        </Form.Group>
                      </Col>

                      {/* C.c recipient */}
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>{i18next.t('cc')}</Form.Label>
                          <CreatableSelect
                            isMulti
                            components={{ DropdownIndicator: null }}
                            placeholder="C.c."
                            noOptionsMessage={() => i18next.t('add_cc_recipients')}
                            value={values.cc}
                            formatCreateLabel={(i) => (emailIsValid(i) ? `Aggiungi ${i}` : `${i}`)}
                            onChange={(newValue: MultiValue<ReactSelectOption>) => {
                              const filtered: ReactSelectOption[] = removeDuplicates(
                                newValue.filter((el) => emailIsValid(el.value))
                              );
                              setFieldValue('cc', filtered);
                              setSendEmailData(prev => ({ ...prev, cc: filtered }))
                            }}
                          />
                          <ValidationError {...{ errors, touched, name: 'cc' }} />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="mt-2">
                      <Col xs={12}>
                        <Form.Group className="mb-3">
                          <Form.Label>{i18next.t('message')}</Form.Label>
                          <ReactQuill
                            theme="snow"
                            placeholder={i18next.t('write_email')}
                            value={values.message}
                            onChange={(message) => {
                              setFieldValue('message', message)
                              setSendEmailData(prev => ({ ...prev, message }))
                            }}
                          />
                          <ValidationError {...{ errors, touched, name: 'message' }} />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>
                )}

                <div className="mt-4">
                  <MultiFileUploader
                    files={uploadFiles}
                    setFiles={(files) => {
                      setUploadFiles(files)
                      updateFunnelData({ files })
                    }}
                    maxSize={25 * 1024 * 1024}
                    types={OPPORTUNITY_SUPPORTED_FILE_TYPES}
                  />
                  <ValidationError {...{ errors, touched, name: 'files' }} />
                </div>
              </Form>
            )
          }}
        </Formik>
      )}
    </div>
  );
};
